import { axios } from 'utils/axios';
import { InfiniteData, useMutation, UseMutationResult } from '@tanstack/react-query';
import { queryClient } from 'utils/react-query';
import { BaseArticle, BaseMedication, ContentType, LikedIds } from '@novaheal/types';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ToastVariant } from 'types/Toast';
import { LikedContentPage } from 'hooks/api/backend/article/useInfiniteLikedContent';

type LikesCount = {
  articles: number;
  medications: number;
};

type MutationData = {
  id: string;
  type: ContentType;
};

export const removeLike = async ({ id }: MutationData): Promise<void> => {
  await axios.delete(`user/like/${id}`);
};

export const useRemoveLike = (): UseMutationResult<void, unknown, MutationData, unknown> => {
  const { show } = useNovaToast();

  return useMutation({
    mutationFn: removeLike,
    // 404 from removeLikeFromUser (nothing to delete) lands here too, same
    // as any other failure — cache stays untouched, user gets a toast
    // instead of the button silently doing nothing.
    onError: () => {
      show({ variant: ToastVariant.ERROR });
    },
    // `type` is passed in by the caller (useIsLiked already knows it — see
    // handleLikeChange) rather than inferred by looking `contentfulId` up
    // in the ['likes'] cache. That inference was the bug: on pages that
    // skip useLikes() via `enabled: false` (Favorites, through
    // entryIsLiked), ['likes'] is never populated, so the lookup silently
    // returned undefined and every patch below got skipped.
    onSuccess: (_data, { id: contentfulId, type }) => {
      queryClient.setQueryData<LikedIds>(['likes'], (current) => {
        if (!current) return current;
        return {
          likedArticles: current.likedArticles.filter((like) => like.contentfulId !== contentfulId),
          likedMedications: current.likedMedications.filter(
            (like) => like.contentfulId !== contentfulId
          ),
        };
      });

      queryClient.setQueryData<LikesCount>(['likes-count'], (current) => {
        if (!current) return current;
        return type === ContentType.ARTICLE
          ? { ...current, articles: Math.max(0, current.articles - 1) }
          : { ...current, medications: Math.max(0, current.medications - 1) };
      });

      // Splice the item straight out of whichever cached Favorites page it's
      // on — no refetch. Invalidating here would re-request pages using
      // skip/take offsets computed before the deletion, which is exactly
      // what caused removing an item from page 2 to come back showing page
      // 1's content instead. We already know which item was removed, so
      // there's nothing left to ask the server for.
      queryClient.setQueryData<InfiniteData<LikedContentPage<BaseArticle | BaseMedication>>>(
        ['liked-content-paginated', type],
        (current) => {
          if (!current) return current;
          return {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              items: page.items.filter((item) => item.id !== contentfulId),
            })),
          };
        }
      );
    },
  });
};
